import { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { OnboardingCurve, OnboardingCurveEvent } from '@/types';

type Normalization = 'invited' | 'onboarded';

interface OnboardingCurveChartProps {
  data: OnboardingCurve;
}

const CURRENT_COLOR = 'hsl(54 100% 63%)';
const PREVIOUS_COLORS = [
  'hsl(199 89% 62%)',
  'hsl(280 70% 68%)',
  'hsl(150 60% 55%)',
  'hsl(12 80% 62%)',
  'hsl(0 0% 65%)',
];

function formatHours(hours: number) {
  const days = Math.floor(hours / 24);
  const rest = Math.floor(hours % 24);
  const minutes = Math.round((hours % 1) * 60);

  if (days > 0) return `${days}d ${rest}h`;
  if (rest > 0) return `${rest}h ${minutes}m`;
  return `${minutes}m`;
}

function seriesValue(
  event: OnboardingCurveEvent,
  normalization: Normalization,
  count: number,
) {
  const total = normalization === 'invited' ? event.invited : event.onboarded;
  if (!total) return 0;
  return (count / total) * 100;
}

interface TooltipEntry {
  name: string;
  value: number;
  color: string;
  dataKey: string;
  payload: Record<string, number | null>;
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: number;
}) {
  if (!active || !payload?.length) return null;

  return (
    <div
      style={{
        backgroundColor: 'hsl(0 0% 9%)',
        border: '1px solid hsl(0 0% 15%)',
        borderRadius: '0.5rem',
        padding: '12px',
        color: 'hsl(0 0% 100%)',
      }}
    >
      <p style={{ marginBottom: '8px', fontWeight: 'bold' }}>
        {formatHours(label ?? 0)} since start
      </p>
      {payload.map((entry) => {
        const count = entry.payload[`${entry.dataKey}__count`];
        return (
          <p key={entry.dataKey} style={{ margin: '4px 0', fontSize: '14px' }}>
            <span style={{ color: entry.color }}>{entry.name}: </span>
            <span style={{ fontWeight: 'bold' }}>
              {entry.value.toFixed(1)}%
            </span>
            {typeof count === 'number' && (
              <span style={{ color: 'hsl(0 0% 65%)' }}> ({count})</span>
            )}
          </p>
        );
      })}
    </div>
  );
}

export function OnboardingCurveChart({ data }: OnboardingCurveChartProps) {
  const [normalization, setNormalization] = useState<Normalization>('invited');
  const [enabledEventIds, setEnabledEventIds] = useState<string[]>(() =>
    data.previousEvents.slice(0, 2).map((event) => event.eventId),
  );

  const series = useMemo(() => {
    const previous = data.previousEvents
      .filter((event) => enabledEventIds.includes(event.eventId))
      .map((event, index) => ({
        event,
        color: PREVIOUS_COLORS[index % PREVIOUS_COLORS.length],
      }));

    const current =
      data.currentEvent.points.length > 0
        ? [{ event: data.currentEvent, color: CURRENT_COLOR }]
        : [];

    return [...current, ...previous];
  }, [data, enabledEventIds]);

  const chartData = useMemo(() => {
    const hours = Array.from(
      new Set(
        series.flatMap(({ event }) => event.points.map((point) => point.hours)),
      ),
    ).sort((a, b) => a - b);

    const cursors = series.map(() => 0);

    return hours.map((hour) => {
      const row: Record<string, number | null> = { hours: hour };

      series.forEach(({ event }, index) => {
        const points = event.points;
        while (
          cursors[index] + 1 < points.length &&
          points[cursors[index] + 1].hours <= hour
        ) {
          cursors[index] += 1;
        }

        const lastHour = points[points.length - 1].hours;
        if (hour > lastHour) {
          row[event.eventId] = null;
          row[`${event.eventId}__count`] = null;
          return;
        }

        const point = points[cursors[index]];
        row[event.eventId] = seriesValue(event, normalization, point.count);
        row[`${event.eventId}__count`] = point.count;
      });

      return row;
    });
  }, [series, normalization]);

  function toggleEvent(eventId: string) {
    setEnabledEventIds((current) =>
      current.includes(eventId)
        ? current.filter((id) => id !== eventId)
        : [...current, eventId],
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground text-sm">Compare with:</span>
        {data.previousEvents.map((event) => {
          const isEnabled = enabledEventIds.includes(event.eventId);
          const colorIndex = data.previousEvents
            .filter((candidate) => enabledEventIds.includes(candidate.eventId))
            .findIndex((candidate) => candidate.eventId === event.eventId);

          return (
            <Button
              key={event.eventId}
              type="button"
              size="sm"
              variant={isEnabled ? 'secondary' : 'outline'}
              onClick={() => toggleEvent(event.eventId)}
              className={cn('gap-2', !isEnabled && 'text-muted-foreground')}
            >
              <span
                className="size-2 rounded-full"
                style={{
                  backgroundColor: isEnabled
                    ? PREVIOUS_COLORS[colorIndex % PREVIOUS_COLORS.length]
                    : 'hsl(0 0% 40%)',
                }}
              />
              {event.eventName}
            </Button>
          );
        })}

        <div className="ml-auto flex items-center gap-2">
          <span className="text-muted-foreground text-sm">Percent of:</span>
          <Button
            type="button"
            size="sm"
            variant={normalization === 'invited' ? 'secondary' : 'outline'}
            onClick={() => setNormalization('invited')}
          >
            Invited
          </Button>
          <Button
            type="button"
            size="sm"
            variant={normalization === 'onboarded' ? 'secondary' : 'outline'}
            onClick={() => setNormalization('onboarded')}
          >
            Final onboarded
          </Button>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={chartData} margin={{ bottom: 30, right: 16 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="hours"
            type="number"
            domain={[0, 'dataMax']}
            tickFormatter={(value: number) => `${Math.round(value)}h`}
            label={{
              value: 'Hours since first onboarding',
              position: 'insideBottom',
              offset: -10,
            }}
          />
          <YAxis
            domain={[0, 100]}
            tickFormatter={(value: number) => `${value}%`}
            label={{ value: '% Onboarded', angle: -90 }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend verticalAlign="top" height={36} />
          {series.map(({ event, color }) => (
            <Line
              key={event.eventId}
              type="monotone"
              dataKey={event.eventId}
              name={event.eventName}
              stroke={color}
              strokeWidth={event.eventId === data.currentEvent.eventId ? 3 : 2}
              dot={event.points.length <= 10}
              connectNulls={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>

      <div className="flex flex-wrap gap-x-6 gap-y-1 text-muted-foreground text-xs">
        {series.map(({ event, color }) => (
          <span key={event.eventId} className="flex items-center gap-2">
            <span
              className="size-2 rounded-full"
              style={{ backgroundColor: color }}
            />
            {event.eventName}: {event.onboarded}/{event.invited} onboarded
            {event.points.length > 0 &&
              ` · ${formatHours(event.points[event.points.length - 1].hours)} elapsed`}
          </span>
        ))}
      </div>
    </div>
  );
}
